// Scrolling to an anchor at hydration time is a race the anchor loses: blocks
// above the target are still fetching (the Book Appointment widget grows by
// ~232px once its slots arrive), so the offset the browser animates towards is
// stale before the animation ends. The viewport stays where it was put and the
// target comes to rest far below where it was asked to be (SH-559).
//
// So don't scroll once — scroll, then keep the target aligned until the layout
// above it stops moving, and get out of the way the moment the user scrolls.

const POLL_MS = 100;

// How long the target's document position must hold still before we accept the
// layout as final.
const STABLE_MS = 300;

// An upper bound on how long we keep correcting. A page that never settles
// (an animation, a carousel) must not be followed forever.
const DEFAULT_TIMEOUT_MS = 2000;

// Deliberately not 'scroll': our own scrollIntoView fires that, which would
// cancel the correction we just made. These are the events only a user emits.
const USER_TAKEOVER_EVENTS = ['wheel', 'touchstart', 'keydown'] as const;

// The position that matters is the target's place in the document, not in the
// viewport: the latter changes on every frame of a smooth scroll, the former
// only when something above the target changes size.
const documentTopOf = (el: HTMLElement) =>
  el.getBoundingClientRect().top + window.scrollY;

export interface ScrollToSettledOptions {
  /** How long to keep re-aligning before giving up. Default 2000ms. */
  timeoutMs?: number;
}

/**
 * Scrolls `getTarget()` into view and holds it there while the page settles.
 *
 * Returns a cancel function; it is also called automatically once the layout
 * is stable, at the timeout, or as soon as the user scrolls.
 */
export const scrollToSettled = (
  getTarget: () => HTMLElement | null,
  { timeoutMs = DEFAULT_TIMEOUT_MS }: ScrollToSettledOptions = {},
): (() => void) => {
  const target = getTarget();
  if (!target) {
    return () => {};
  }

  target.scrollIntoView({ behavior: 'smooth' });

  let lastTop = documentTopOf(target);
  let stableMs = 0;

  const stop = () => {
    clearInterval(poll);
    clearTimeout(deadline);
    for (const event of USER_TAKEOVER_EVENTS) {
      window.removeEventListener(event, stop);
    }
  };

  const poll = setInterval(() => {
    const current = getTarget();
    if (!current) {
      stop();
      return;
    }

    const top = documentTopOf(current);
    if (top === lastTop) {
      stableMs += POLL_MS;
      if (stableMs >= STABLE_MS) {
        stop();
      }
      return;
    }

    lastTop = top;
    stableMs = 0;
    // 'instant' on purpose: globals.css sets `scroll-behavior: smooth` on
    // <html>, so a correction would otherwise animate and read as a second,
    // jumpier scroll rather than as the first one landing properly.
    current.scrollIntoView({ behavior: 'instant' });
  }, POLL_MS);

  const deadline = setTimeout(stop, timeoutMs);

  for (const event of USER_TAKEOVER_EVENTS) {
    window.addEventListener(event, stop, { passive: true });
  }

  return stop;
};
